import { type CustomNode, NodeType } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';

/**
 * Graph reachability helpers for the rerun-from-here FE flow.
 *
 * MUST stay in sync with the BE's ``temporal/utils/downstream.py``
 * (``build_downstream_set``).  Both must agree on which nodes are
 * "execution nodes" (excluding tool/memory handle nodes) and on the
 * forward-reachability semantics, otherwise the FE's "N downstream
 * nodes will recompute" preview will not match what the BE actually
 * invalidates.
 *
 * Pure functions; no React, no module state -- safe to call from
 * memoised derivations and trivial to unit test.
 */

/**
 * Tool / memory nodes are *attached* to agent nodes via handle edges
 * but never execute on their own.  They MUST be excluded from the
 * downstream walk because:
 *
 *   * They have no result row to invalidate.
 *   * Walking through them would make every agent that shares a
 *     tool look "downstream" of every other agent that shares the
 *     same tool, which is wrong.
 *
 * Mirrors the BE check: handle nodes are filtered out of the
 * execution set in ``_forward_adjacency``.
 */
export function isHandleNode(node: CustomNode): boolean {
  const t = node.data?.struct?.node_type;
  return t === 'tool' || t === 'memory';
}

/**
 * Set of every execution-node id reachable forward from any node in
 * ``startingNodeIds``, optionally including the starting nodes
 * themselves.  Edges that touch a handle node on either end are
 * skipped (see ``isHandleNode``).
 *
 * Args:
 *   startingNodeIds: roots of the walk (react_flow_generated_id values).
 *   nodes: current canvas nodes.  Used only to identify the
 *     execution-node set (handle nodes are excluded).
 *   edges: current canvas edges.  Walked forward via ``source`` ->
 *     ``target`` (the React Flow standard fields).
 *   includeSelf: whether to include the roots in the returned set.
 *     Pass ``true`` for "invalidation set"; ``false`` for "strictly
 *     downstream".
 */
export function buildDownstreamSet(
  startingNodeIds: readonly string[],
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  includeSelf: boolean,
): Set<string> {
  const executionIds = new Set<string>();
  for (const n of nodes) {
    if (!isHandleNode(n) && n.type !== NodeType.ArtifactNode) {
      executionIds.add(n.id);
    }
  }

  const roots = new Set<string>(
    startingNodeIds.filter((id) => executionIds.has(id)),
  );
  if (roots.size === 0) return new Set<string>();

  const adjacency = new Map<string, string[]>();
  for (const e of edges) {
    const src = e.source;
    const tgt = e.target;
    if (executionIds.has(src) && executionIds.has(tgt)) {
      const list = adjacency.get(src);
      if (list) list.push(tgt);
      else adjacency.set(src, [tgt]);
    }
  }

  const visited = new Set<string>();
  const stack: string[] = Array.from(roots);
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (visited.has(current)) continue;
    visited.add(current);
    const neighbours = adjacency.get(current);
    if (!neighbours) continue;
    for (const n of neighbours) {
      if (!visited.has(n)) stack.push(n);
    }
  }

  if (!includeSelf) {
    for (const root of roots) visited.delete(root);
  }
  return visited;
}
